import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import { ViewToggle } from './ViewToggle';
import { QRAnalyticsService } from '../../../services/qrAnalyticsService';
import { useAuth } from '../../../contexts/AuthContext';

const QRCodeLibraryHeader = ({
  searchTerm,
  onSearchChange,
  selectedQRs = [],
  onBulkAction,
  statusFilter,
  onStatusFilterChange,
  typeFilter,
  onTypeFilterChange,
  campaignFilter,
  onCampaignFilterChange,
  dateRange,
  onDateRangeChange,
  onCreateNew,
  selectedCount,
  currentView,
  onViewChange,
  isTrashView = false,
  onTrashToggle
}) => {
  const { user, loading: authLoading } = useAuth();
  const [totalQRCodes, setTotalQRCodes] = useState(0);
  const [activeQRCodes, setActiveQRCodes] = useState(0);
  const [totalScans, setTotalScans] = useState(0);
  const [loading, setLoading] = useState(true);

  // Load real statistics with proper error handling
  useEffect(() => {
    if (!authLoading) {
      loadQRStatistics();
    }
  }, [user, authLoading]);

  const loadQRStatistics = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Get QR codes data with better error handling
      const result = await QRAnalyticsService?.getUserQRCodes?.(1000);
      
      if (result?.error) {
        console.log('Error loading QR statistics:', result?.error);
        setTotalQRCodes(0);
        setActiveQRCodes(0);
        setTotalScans(0);
      } else {
        const codes = result?.data || [];
        const activeCodes = codes?.filter(qr => qr?.isValid !== false) || [];
        const totalScanCount = codes?.reduce((sum, qr) => sum + (qr?.currentScans || 0), 0);
        
        setTotalQRCodes(codes?.length || 0);
        setActiveQRCodes(activeCodes?.length || 0);
        setTotalScans(totalScanCount || 0);
      }
    } catch (error) {
      console.error('Failed to load QR statistics:', error);
      setTotalQRCodes(0);
      setActiveQRCodes(0);
      setTotalScans(0);
    } finally {
      setLoading(false);
    }
  };

  // Show loading state during auth initialization
  if (authLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {[1, 2, 3]?.map((i) => (
            <div key={i} className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="space-y-2 flex-1">
                  <div className="w-24 h-4 bg-gray-200 rounded"></div>
                  <div className="w-16 h-8 bg-gray-200 rounded"></div>
                </div>
                <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
              </div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4]?.map((i) => (
            <div key={i} className="space-y-2">
              <div className="w-20 h-4 bg-gray-200 rounded"></div>
              <div className="w-full h-10 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total QR Codes</p>
              <div className="text-2xl font-bold text-gray-900">
                {loading ? (
                  <div className="w-16 h-8 bg-gray-200 rounded animate-pulse"></div>
                ) : (
                  (totalQRCodes || 0)?.toLocaleString()
                )}
              </div>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <Icon name="QrCode" size={24} className="text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active QR Codes</p>
              <div className="text-2xl font-bold text-green-600">
                {loading ? (
                  <div className="w-16 h-8 bg-gray-200 rounded animate-pulse"></div>
                ) : (
                  (activeQRCodes || 0)?.toLocaleString()
                )}
              </div>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <Icon name="CheckCircle" size={24} className="text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Scans</p>
              <div className="text-2xl font-bold text-purple-600">
                {loading ? (
                  <div className="w-16 h-8 bg-gray-200 rounded animate-pulse"></div>
                ) : (
                  (totalScans || 0)?.toLocaleString()
                )}
              </div>
            </div>
            <div className="p-3 bg-purple-100 rounded-full">
              <Icon name="BarChart3" size={24} className="text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Search, Filters, and View Toggle */}
      <div className="space-y-4">
        {/* Search bar with View Toggle */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex-1 max-w-md">
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
              Search QR Codes
            </label>
            <input
              id="search"
              type="text"
              value={searchTerm || ''}
              onChange={(e) => onSearchChange?.(e?.target?.value)}
              placeholder="Search by QR code name, URL, or content..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          {/* View Toggle and Trash Button */}
          <div className="flex items-center gap-3">
            {/* View Toggle */}
            <ViewToggle 
              currentView={currentView} 
              onViewChange={onViewChange}
            />
            
            {/* Trash Button */}
            <button
              onClick={() => onTrashToggle?.(!isTrashView)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                isTrashView 
                  ? 'bg-blue-600 text-white border border-blue-600' 
                  : 'bg-gray-700 text-white border border-gray-700 hover:bg-gray-800'
              }`}
            >
              <Icon name="Trash2" size={16} />
              <span className="font-medium">{isTrashView ? 'Close Trash' : 'Trash'}</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
          <div className="space-y-2">
            <label htmlFor="type" className="block text-sm font-medium text-gray-700">
              QR Type
            </label>
            <Select
              id="type"
              value={typeFilter || 'all'}
              onChange={(value) => onTypeFilterChange?.(value)}
              options={[
                { value: 'all', label: 'All Types' },
                { value: 'url', label: 'URL' },
                { value: 'text', label: 'Plain Text' },
                { value: 'email', label: 'Email' },
                { value: 'phone', label: 'Phone' },
                { value: 'sms', label: 'SMS' },
                { value: 'wifi', label: 'WiFi' },
                { value: 'vcard', label: 'Contact Card' },
                { value: 'whatsapp', label: 'WhatsApp' }
              ]}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRCodeLibraryHeader;